import { Component, Inject, OnInit } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef
} from '@angular/material/dialog';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ModalAcessoRestritoComponent } from '../../saida-gate/modal-acesso-restrito/modal-acesso-restrito.component';
import {
  IGetPagamentoSaidaCaixa,
  IUpdatePagamentoSaidaCaixaBatch
} from 'src/app/services/PagamentoSaida/pagamentoSaida.model';
import { PagamentoSaidaService } from 'src/app/services/PagamentoSaida/pagamentoSaida.service';
import { SwalAlertService } from 'src/app/services/SwalAlertService/swalAlert.service';

type IOptions = {
  name: string;
  value: string | number | null;
};

@Component({
  selector: 'app-modal-pagamento-caixa',
  templateUrl: './modal-pagamento-caixa.component.html',
  styleUrls: ['./modal-pagamento-caixa.component.scss']
})
export class ModalPagamentoCaixaComponent implements OnInit {
  form: FormGroup;
  isLoading = false;
  fkServicoEntradaPagamentoSaida = 0;
  descontoAgendamento = 0;
  private valorBaseServico = 0;
  private acrescimoInicial = 0;
  private descontoInicial = 0;

  modoPagamentoOptions: IOptions[] = [
    { name: 'Cartão de Crédito', value: 1 },
    { name: 'Cartão de Débito', value: 19 },
    { name: 'Pix', value: 27 },
    { name: 'Dinheiro', value: 9 },
    { name: 'A Prazo', value: 28 }
  ];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialog: MatDialog,
    public dialogRef: MatDialogRef<ModalAcessoRestritoComponent>,
    private fb: FormBuilder,
    private pagamentoSaidaService: PagamentoSaidaService,
    public swalAlert: SwalAlertService
  ) {
    this.form = this.initForm();
  }

  ngOnInit(): void {
    this.form.get('VL_ACRM_CAIX')?.valueChanges.subscribe(() => this.calcValores());
    this.form.get('VL_DSCT_CAIX')?.valueChanges.subscribe(() => this.calcValores());
    this.loadPagamentos();
  }

  initForm(): FormGroup {
    return this.fb.group({
      ServicoPagamentoTipoValor: this.fb.array([]),
      VL_ACRM_CAIX: [0],
      VL_DSCT_CAIX: [0],
      NU_TIPO_PGMT_CAIX: [0],
      VL_PGMT_SAID_CAIX: [0],
      VL_TOTL: [0],
      VL_REST: [0]
    });
  }

  get ServicoPagamentoTipoValor(): FormArray {
    return this.form.get('ServicoPagamentoTipoValor') as FormArray;
  }

  loadPagamentos(): void {
    if (!this.data.EDIT_ITEM || !this.data.PK_PGMT_SAID) {
      return;
    }

    this.isLoading = true;
    this.pagamentoSaidaService
      .GetPagamentoSaidaCaixa({ idPagamentoSaida: this.data.PK_PGMT_SAID })
      .subscribe({
        next: initial => {
          const first = initial[0];
          if (!first?.FK_SRVC_ENTR_PGMT_SAID) {
            this.isLoading = false;
            return;
          }

          this.fkServicoEntradaPagamentoSaida = first.FK_SRVC_ENTR_PGMT_SAID;

          this.pagamentoSaidaService
            .GetPagamentoSaidaCaixa({
              idServicoEntradaPagamentoSaida: this.fkServicoEntradaPagamentoSaida
            })
            .subscribe({
              next: pagamentos => this.applyPagamentos(pagamentos),
              error: () => (this.isLoading = false)
            });
        },
        error: () => (this.isLoading = false)
      });
  }

  applyPagamentos(pagamentos: IGetPagamentoSaidaCaixa[]): void {
    // Uma linha por forma de pagamento (evita duplicar valor quando há vários serviços).
    const unicos = Array.from(
      new Map(
        (pagamentos || [])
          .filter(p => !!p?.PK_PGMT_SAID)
          .map(p => [p.PK_PGMT_SAID, p])
      ).values()
    );

    const first = unicos[0];
    this.acrescimoInicial = Number(first?.VL_ACRM_CAIX ?? 0);
    this.descontoInicial = Number(first?.VL_DSCT_CAIX ?? 0);
    // valorBase já é a soma das formas (já inclui acréscimo/desconto aplicados na criação).
    this.valorBaseServico = unicos.reduce(
      (acc, p) => acc + Number(p.VL_PGMT_SAID_CAIX || 0),
      0
    );

    this.form.patchValue({
      VL_ACRM_CAIX: this.acrescimoInicial,
      VL_DSCT_CAIX: this.descontoInicial
    });

    this.ServicoPagamentoTipoValor.clear();
    unicos.forEach(p => {
      const option = this.modoPagamentoOptions.find(
        o => Number(o.value) === Number(p.NU_TIPO_PGMT_CAIX)
      );
      this.ServicoPagamentoTipoValor.push(
        this.fb.group({
          PK_PGMT_SAID: [p.PK_PGMT_SAID],
          NM_TIPO_PGMT: [option?.name ?? ''],
          NU_TIPO_PGMT_CAIX: [p.NU_TIPO_PGMT_CAIX, Validators.required],
          VL_PGMT_SAID_CAIX: [p.VL_PGMT_SAID_CAIX, Validators.required]
        })
      );
    });

    this.descontoAgendamento = 0;
    this.calcValores();
    this.isLoading = false;
  }

  calcValores(): void {
    const acrescimo = Number(this.form.get('VL_ACRM_CAIX')?.value || 0);
    const desconto = Number(this.form.get('VL_DSCT_CAIX')?.value || 0);

    // Só aplica a variação de acréscimo/desconto em relação ao que já estava salvo.
    // Não subtrai desconto de agendamento de novo: ele já está embutido no valorBase.
    const valorTotal = Math.max(
      this.valorBaseServico +
        (acrescimo - this.acrescimoInicial) -
        (desconto - this.descontoInicial),
      0
    );
    this.form.get('VL_TOTL')?.setValue(valorTotal);
    this.calcValorRestante();
  }

  calcValorRestante(): void {
    const valorTotal = Number(this.form.get('VL_TOTL')?.value || 0);
    const valorAdicionado = this.ServicoPagamentoTipoValor.value.reduce(
      (acc: number, item: any) => acc + Number(item.VL_PGMT_SAID_CAIX || 0),
      0
    );
    let valorRestante = Number((valorTotal - valorAdicionado).toFixed(2));
    valorRestante = Math.max(valorRestante, 0);
    this.form.get('VL_REST')?.setValue(valorRestante);
    this.form.get('VL_PGMT_SAID_CAIX')?.setValue(valorRestante, { emitEvent: false });
  }

  addPagamento(): void {
    const valorRestante = Number(this.form.get('VL_REST')?.value || 0);
    const valorParaAdicionar = Number(this.form.get('VL_PGMT_SAID_CAIX')?.value || 0);
    const tipoPagamento = Number(this.form.get('NU_TIPO_PGMT_CAIX')?.value || 0);

    if (!tipoPagamento || !valorParaAdicionar) {
      return;
    }

    if (valorParaAdicionar > valorRestante) {
      this.swalAlert.showAlert(
        'Atenção!',
        'O valor do pagamento não pode ser maior que o valor restante!',
        'warning',
        true,
        false
      );
      return;
    }

    const pagamento = this.modoPagamentoOptions.find(
      option => Number(option.value) === tipoPagamento
    );
    if (!pagamento) {
      return;
    }

    this.ServicoPagamentoTipoValor.push(
      this.fb.group({
        PK_PGMT_SAID: [null],
        NM_TIPO_PGMT: [pagamento.name],
        NU_TIPO_PGMT_CAIX: [pagamento.value, Validators.required],
        VL_PGMT_SAID_CAIX: [valorParaAdicionar, Validators.required]
      })
    );

    this.form.patchValue({ NU_TIPO_PGMT_CAIX: 0, VL_PGMT_SAID_CAIX: 0 });
    this.calcValorRestante();
  }

  removePagamento(index: number): void {
    this.ServicoPagamentoTipoValor.removeAt(index);
    this.calcValores();
  }

  onChangeModoPagamento(event: number): void {
    this.form.get('NU_TIPO_PGMT_CAIX')?.setValue(event);
  }

  postPagamentoSaida(): void {
    if (this.disableConfirmar()) {
      return;
    }

    this.isLoading = true;
    const payload = this.createPayload();

    this.pagamentoSaidaService.UpdatePagamentoSaidaCaixaBatch(payload).subscribe({
      next: () => {
        this.isLoading = false;
        this.dialogRef.close(true);
      },
      error: err => {
        this.isLoading = false;
        const msg =
          err?.error?.Errors?.Error ??
          err?.error?.Error ??
          'Não foi possível salvar o pagamento.';
        this.swalAlert.showAlert('Erro!', msg, 'warning');
      }
    });
  }

  createPayload(): IUpdatePagamentoSaidaCaixaBatch {
    return {
      FK_SRVC_ENTR_PGMT_SAID: this.fkServicoEntradaPagamentoSaida,
      VL_ACRM_CAIX: Number(this.form.get('VL_ACRM_CAIX')?.value || 0),
      VL_DSCT_CAIX: Number(this.form.get('VL_DSCT_CAIX')?.value || 0),
      Pagamentos: this.ServicoPagamentoTipoValor.value.map((item: any) => ({
        PK_PGMT_SAID: item.PK_PGMT_SAID || undefined,
        NU_TIPO_PGMT_CAIX: Number(item.NU_TIPO_PGMT_CAIX),
        VL_PGMT_SAID_CAIX: Number(item.VL_PGMT_SAID_CAIX)
      }))
    };
  }

  disableConfirmar(): boolean {
    const valorRestante = Number(this.form.get('VL_REST')?.value || 0);
    return (
      !this.fkServicoEntradaPagamentoSaida ||
      this.ServicoPagamentoTipoValor.length === 0 ||
      valorRestante !== 0
    );
  }

  onCancelar(): void {
    this.dialogRef.close();
  }
}
